import { PublicService } from '@/models/public/bookingWidget.model'
import {
  DrivingSegmentPayload,
  Route,
  ServerRoute,
} from '@/models/public/routePlanner.model'
import { addMinutes } from 'date-fns'
import _ from 'lodash'

export const calculateServiceETA = (
  service: PublicService | null,
  route: Route | null,
): Date | null => {
  if (!route || !service) return null

  const applicableRouteStop = route.stops?.find(
    (stop) => stop.service.id === service.id,
  )
  if (!route.stops?.length || applicableRouteStop?.order_index === 0) {
    if (typeof route.start_time === 'string') {
      route.start_time = new Date(route.start_time)
    }
    if (route.start_time && route.segments?.length) {
      return addMinutes(route.start_time, route.segments[0].duration_minutes)
    } else if (route.start_time) {
      return route.start_time
    } else {
      return null
    }
  } else if (applicableRouteStop && applicableRouteStop?.order_index > 0) {
    const previousRouteStop = route.stops?.find(
      (stop) => stop.order_index === applicableRouteStop.order_index - 1,
    )
    const drivingSegmentFromPreviousStop = route.segments.find(
      (segment) =>
        segment.to_location.id === applicableRouteStop.service.location.id,
    )
    if (typeof previousRouteStop?.end_time === 'string') {
      previousRouteStop.end_time = new Date(previousRouteStop.end_time)
    }
    if (previousRouteStop?.end_time && drivingSegmentFromPreviousStop) {
      return addMinutes(
        previousRouteStop.end_time,
        drivingSegmentFromPreviousStop.duration_minutes,
      )
    } else if (previousRouteStop?.end_time) {
      return previousRouteStop.end_time
    } else {
      return null
    }
  }
  return null
}

export const calculateFinalDestinationETA = (
  route: Route | null,
): Date | null => {
  if (!route) {
    return null
  }
  if (!route.stops.length) {
    if (typeof route.start_time === 'string') {
      route.start_time = new Date(route.start_time)
    }
    // Take into account only start time and first drive
    if (route.start_time && route.segments?.length) {
      return addMinutes(route.start_time, route.segments[0].duration_minutes)
    } else if (route.start_time) {
      return route.start_time
    }
    return null
  }
  const lastRouteStop = route.stops[route.stops.length - 1]
  const drivingSegmentFromLastStop = route.segments.find(
    (segment) => segment.from_stop && segment.from_stop.id === lastRouteStop.id,
  )
  if (lastRouteStop?.end_time && drivingSegmentFromLastStop) {
    return addMinutes(
      lastRouteStop.end_time,
      drivingSegmentFromLastStop.duration_minutes,
    )
  } else if (lastRouteStop?.end_time) {
    return lastRouteStop.end_time
  }
  return null
}

export const calculateNewSegments = (
  googleMapsRoute: {
    legs: { duration: { value: number }; distance: { value: number } }[]
  },
  route: Route | null,
): DrivingSegmentPayload[] => {
  if (!route || !route.start_point || !route.end_point) {
    return []
  }
  return googleMapsRoute.legs.map((leg, index) => {
    const segment: DrivingSegmentPayload = {
      route: route.id,
      from_stop: null,
      to_stop: null,
      from_location: null,
      to_location: null,
      duration_minutes: _.round(leg.duration.value / 60), // Originally in seconds
      distance_km: _.round(leg.distance.value / 1000, 2), // Originally in meters
    }
    if (index === 0) {
      // First leg from route start point
      segment.from_location = route.start_point!.id
      if (googleMapsRoute.legs.length === 1) {
        // No stops
        segment.to_location = route.end_point!.id
      } else {
        const firstStop = route.stops[0]
        segment.to_stop = firstStop.id
        segment.to_location = firstStop.service.location.id
      }
    } else if (index === googleMapsRoute.legs.length - 1) {
      // Last leg to route end point
      segment.to_location = route.end_point!.id
      if (googleMapsRoute.legs.length === 1) {
        // No stops
        segment.from_location = route.start_point!.id
      } else {
        const lastStop = route.stops[route.stops.length - 1]
        segment.from_stop = lastStop.id
        segment.from_location = lastStop.service.location.id
      }
    } else {
      segment.from_location = route.stops[index - 1]!.service.location.id
      segment.to_location = route.stops[index]!.service.location.id
      segment.from_stop = route.stops[index - 1]!.id
      segment.to_stop = route.stops[index]!.id
    }
    return segment
  })
}

export const parseRoute = (
  serverRoute: ServerRoute,
  useCurrentTime = false,
): Route => {
  const route: Route = {
    ...serverRoute,
    start_time: serverRoute.start_time
      ? new Date(serverRoute.start_time)
      : null,
    stops: serverRoute.stops.map((stop) => ({
      ...stop,
      start_time: stop.start_time ? new Date(stop.start_time) : null,
      end_time: stop.end_time ? new Date(stop.end_time) : null,
    })),
  }

  // Override start_time with current time if requested (for existing routes)
  if (useCurrentTime) {
    route.start_time = new Date()
  }

  return route
}
export const extractAddressInfo = (route: Route) => {
  const startPoint = route.start_point
    ? {
        city: '',
        country: '',
        county: '',
        google_address: route.start_point.full_address,
        latitude: route.start_point.latitude_decimal_degree,
        longitude: route.start_point.longitude_decimal_degree,
        number: '',
        street: '',
        zip_code: '',
      }
    : null

  const endPoint = route.end_point
    ? {
        city: '',
        country: '',
        county: '',
        google_address: route.end_point.full_address,
        latitude: route.end_point.latitude_decimal_degree,
        longitude: route.end_point.longitude_decimal_degree,
        number: '',
        street: '',
        zip_code: '',
      }
    : null

  return { startPoint, endPoint }
}

export const extractServiceInfos = (route: Route) => {
  const serviceInfos: Record<number, any> = {}
  if (Object.keys(serviceInfos).length === 0 && route.segments) {
    route.segments.forEach((segment) => {
      if (segment.to_stop?.start_time && segment.to_stop?.end_time) {
        serviceInfos[segment.to_stop.service.id] = {
          start_time: new Date(segment.to_stop.start_time),
          end_time: new Date(segment.to_stop.end_time),
        }
      }

      if (segment.from_stop?.start_time && segment.from_stop?.end_time) {
        serviceInfos[segment.from_stop.service.id] = {
          start_time: new Date(segment.from_stop.start_time),
          end_time: new Date(segment.from_stop.end_time),
        }
      }
    })
  }

  return serviceInfos
}

export const getRouteGoogleMapsUrl = (route: Route | null): string | null => {
  if (!route || !route.start_point || !route.end_point) {
    return null
  }
  const url = new URL('https://www.google.com/maps/dir/?api=1')
  const waypoints = route.stops
    ?.map((stop) => stop.service.location.full_address)
    .join('|')
  url.searchParams.set('origin', route.start_point.full_address)
  url.searchParams.set('destination', route.end_point.full_address)
  url.searchParams.set('travelmode', 'driving')
  if (waypoints.length) {
    url.searchParams.set('waypoints', waypoints)
  }
  return url.href
}
